import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { isMobile } from 'react-device-detect';
import { PartyModel } from 'models/PartyModel';
import { Option } from 'models/common';
import { BLModel } from 'models/BLModel';
import Api from 'utils/api';
import { CustomSet, numberToColor, scrollByHash } from 'utils/common';


const useBlFilter = (id: number) => {
    const {t} = useTranslation('cars');
    const [bls, setBls] = useState<BLModel[]>([]);
    const [editable, setEditable] = useState<any[]>([]);
    const [shipperFilterOptions, setShipperFilterOptions] = useState<Option[]>([]);
    const [consigneeFilterOptions, setConsigneeFilterOptions] = useState<Option[]>([]);
    const [notifyFilterOptions, setNotifyFilterOptions] = useState<Option[]>([]);
    const [filter, setFilter] = useQueryParams({
        shipper: StringParam,
        consignee: NumberParam,
        notify: NumberParam,
    });

    const load = useCallback(() => {
        Api.bl.getBLs(Number(id))
            .then(data => {
                setBls(data.data.map((bl: BLModel) => ({...bl, color: numberToColor(bl.car?.id || 0)})));
                setEditable(old => old.length ? data.data : old);

                const companies = [...new CustomSet<BLModel['shipper']>(
                    (a, b) => a.name === b.name,
                    data.data.map((bl: BLModel) => bl.shipper)
                )];

                setShipperFilterOptions([
                    {value: '', label: t('any')},
                    ...companies.map(c => ({value: c.name, label: c.name}))
                        .sort((a, b) => a.label.localeCompare(b.label))
                ]);

                const consignees = [...new CustomSet<PartyModel>(
                    (a, b) => a.id === b.id,
                    data.data.filter((bl: BLModel) => bl.consignee)
                        .map((bl: BLModel) => bl.consignee)
                )];

                setConsigneeFilterOptions([
                    {value: '', label: t('any')},
                    ...consignees.map(c => ({value: c.id, label: c.shortName}))
                        .sort((a, b) => a.label.localeCompare(b.label))
                ]);

                const notifies = [...new CustomSet<PartyModel>(
                    (a, b) => a.id === b.id,
                    data.data.filter((bl: BLModel) => bl.notify)
                        .map((bl: BLModel) => bl.notify)
                )];

                setNotifyFilterOptions([
                    {value: '', label: t('any')},
                    ...notifies.map(c => ({value: c.id, label: c.shortName}))
                        .sort((a, b) => a.label.localeCompare(b.label))
                ]);
            })
            .then(() => setTimeout(scrollByHash, 100));
    }, [id]);

    const changeFilter = (key: string, value: string | number | string[]) => {
        !isMobile && setFilter(old => ({
            ...old,
            [key]: value === '' || (Array.isArray(value) && value.length === 0) ? undefined : value
        }))
    };

    const filtered = useMemo(() => bls.filter(bl =>
        (filter.shipper === undefined || filter.shipper === bl.shipper.name)
        && (filter.consignee === undefined || filter.consignee === bl.consignee?.id)
        && (filter.notify === undefined || filter.notify === bl.notify?.id)
    ), [bls, filter]);

    return {
        load,
        bls: filtered,
        setBls,
        editable,
        setEditable,
        filter,
        changeFilter,
        shipperFilterOptions,
        consigneeFilterOptions,
        notifyFilterOptions,
    };
};

export default useBlFilter;
